<template>
  <div class="gmf-cheevo-details" v-if="achievement">
    <h2 class="gmf-header">{{ name }}</h2>
    <div class="gmf-cheevo-description gmf-text gmf-mt-2" v-if="achievement.description">{{ achievement.description }}
    </div>
    <AchievementImage :achievement="achievement" :key="achievement.id" />

    <div class="gmf-cheevo-details-track gmf-mb-4" v-if="achievement.maxLevel > 1">
      <div class="gmf-text-small">{{ levelProgressionLabel }}</div>
      <MiniLevelTrack :level="achievement.level" :maxLevel="achievement.maxLevel" :maxNumDots="6" clickable
        @select="onLevelClick" :levelData="levelData" />
    </div>

    <!--
    @TODO remove unused styles and i18n phrases when removing this old goal list
    <div class="gmf-cheevo-goal-list" v-if="goalCount > 0">
      <div class="gmf-cheevo-goals-header">{{ completedGoalCount }} / {{ goalCount }} {{ t('finished_goal') }}</div>
      <div class="gmf-text-small" :class="{ 'gmf-cheevo-goal-completed': isUnlocked || goal.progress >= goal.targetProgressValue }"
        v-for="goal in achievement.goals" :key="goal">
        <div class="gmf-cheevo-goal-label" v-html="md(goal.label)"></div>
      </div>
    </div>-->

    <!-- intentionally using "quest" goal list because it's already styled. Ideally we'd rename to something more generic (and use a component) -->
    <table class="gmf-quest-goal-list" v-if="goalCount > 0">
      <div class="gmf-quest-goal" v-for="(goal, i) in achievement.goals" :key="i">
        <div class="gmf-quest-goal-label">{{ goal.label }}</div>
        <div class="gmf-quest-goal-progress" v-if="!achievement.achievedAt && isMyAchievement">
          <ProgressBar :min="0" :max="goal.targetProgressValue" :value="goal.progress" />
          <div class="gmf-quest-goal-progress-number">{{ Math.min(goal.progress, goal.targetProgressValue) }} / {{ goal.targetProgressValue }}</div>
        </div>
      </div>
    </table>

  </div>
</template>

<style>
.gmf-cheevo-details {
  max-width: 100%;
  width: 440px;
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-3);
  align-items: center;
  justify-content: center;
  text-align: center;
}

.gmf-cheevo-details .gmf-header {
  padding-left: 20px;
  padding-right: 20px;
  margin: 0 !important;
}

.gmf-cheevo-details>.gmf-achievement-image {
  max-width: 70%;
  width: 300px;
  align-self: center;
}

.gmf-cheevo-goals-header {
  font-weight: bold;
  color: var(--gmf-col-black);
  margin-bottom: var(--gmf-p-2);
}

.gmf-cheevo-goal-list {
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-2);
}

.gmf-cheevo-goal-completed {
  text-decoration: line-through;
  color: var(--gmf-col-gray);
}

.gmf-cheevo-details-track {
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-3);
  align-items: center;
}

.gmf-cheevo-details-track .gmf-mini-lv-track {
  --gap: 10px;
  --line: 8px;
  --dot-size: 20px;
}

.gmf-cheevo-details .gmf-quest-goal-list {
  max-width: 300px;
  margin-bottom: var(--gmf-p-4);
}
.gmf-cheevo-details .gmf-quest-goal-list, .gmf-quest-goal-progress-number {
  background: none !important; /* kill old css hacks */
}

</style>

<script>
import callApi from '@/utils/callApi';
import md from '@/utils/md';
import state from '@/state';
const t = state.t.prefix('achievements');
import advancedString from '@/utils/advancedString';

import AchievementImage from '@/components/AchievementImage.vue';
import MiniLevelTrack from '@/components/MiniLevelTrack.vue';
import ProgressBar from '@/components/ProgressBar.vue';

export default {
  inject: ['initialAchievement', 'merchantCustomerId'],
  name: 'AchievementDetails',
  data() {
    return {
      achievement: null,
      tierLadder: [],
    };
  },
  mounted() {
    this.loadDetails(this.initialAchievement);
  },
  computed: {
    name() {
      let name = this.achievement.name.trim();

      // If ends with - Nivå x or - Level x, etc, remove it
      name = name.replace(/ - (Nivå|Level|Lvl\.|Lv.) \d+$/, '');
      name = name.replace(/(Nivå|Level|Lvl\.|Lv.) \d+$/, '');

      return name.trim();
    },
    levelData() {
      const map = {};
      let i = 1;
      for (const ach of this.tierLadder) {
        map[i++] = {
          color: ach.tier.color,
          icon: ach.tier.icon,
        };
      }
      return map;
    },
    isUnlocked() {
      return this.achievement && this.achievement.achievedAt;
    },
    levelProgressionLabel() {
      const { level, maxLevel } = this.achievement;

      return advancedString(t('level_progression'), {
        level,
        maxLevel,
      });
    },
    tierLadderCurrentIndex() {
      for (let i = 0; i < this.tierLadder.length; ++i) {
        if (this.tierLadder[i].id === this.achievement.id) {
          return i;
        }
      }
      return -1;
    },
    nextTier() {
      return this.tierLadder[this.tierLadderCurrentIndex + 1] || null;
    },
    prevTier() {
      return this.tierLadder[this.tierLadderCurrentIndex - 1] || null;
    },
    goalCount() {
      return this.achievement.goals.length;
    },
    completedGoalCount() {
      if (this.isUnlocked) {
        return this.goalCount;
      }
      let count = 0;
      for (const goal of this.achievement.goals) {
        if (goal.progress >= goal.targetProgressValue) {
          ++count;
        }
      }
      return count;
    },
    isMyAchievement() {
      if (! this.merchantCustomerId) { // legacy mode without merchantCustomerId prop
        return true;
      }
      return state.profile?.id === this.merchantCustomerId;
    },
  },
  methods: {
    t,
    md,
    onLevelClick(level) {
      const ach = this.tierLadder[level - 1];
      if (!ach) {
        console.error('Missing level ' + level + ' in tier ladder');
        return;
      }
      this.loadDetails(ach.id);
    },
    loadDetails(id) {
      if (id instanceof Object) {
        if (!this.achievement) {
          this.achievement = id;
        }
        id = id.id;
      } else if (!this.achievement) {
        this.achievement = null;
      }

      let merchantCustomerId = this.merchantCustomerId;
      if (! merchantCustomerId) {
        merchantCustomerId = state.profile?.id;
        if (! merchantCustomerId) {
          console.error('No merchantCustomerId');
          return;
        }
      }

      //callApi('GET', '/v1/client/achievement-details/' + id)
      callApi('GET', `/v1/client/customer-achievements/${merchantCustomerId}/${id}`)
      .then(res => {
          if (res.status == 401) {
            return;
          }
          res.json().then(data => {
            this.achievement = data.achievement;
            this.tierLadder = data.tierLadder;
          });
        });
    }
  },
  components: {
    AchievementImage,
    MiniLevelTrack,
    ProgressBar,
  },
};
</script>
